"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ChevronDownIcon, XIcon, SearchIcon, CheckIcon } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface SingleSelectProps {
  id?: string;
  options?: Array<Option>;
  selectedValue?: Option;
  onChange?: (option: Option | undefined) => void;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptySearchText?: string;
  allowClear?: boolean;
}

export default function SingleSelect({
  id,
  options = [],
  selectedValue,
  onChange,
  label = "Selecionar",
  placeholder = "Selecione uma opção",
  searchPlaceholder = "Pesquisar...",
  emptySearchText = "Nenhum resultado encontrado",
  allowClear = true,
}: SingleSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(selectedValue);
  const [query, setQuery] = useState("");
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setSelectedOption(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    // Limpa a busca ao fechar
    if (!isOpen) setQuery("");
    // Foca no input ao abrir
    const t = setTimeout(() => {
      if (isOpen) searchRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  const handleSelect = (option: Option) => {
    setSelectedOption(option);
    onChange?.(option);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedOption(undefined);
    onChange?.(undefined);
    // Mantém popover aberto para permitir nova seleção
    setQuery("");
    searchRef.current?.focus();
  };

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </Label>
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            ref={buttonRef}
            id={id}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            className={cn(
              "flex items-center w-full h-8 px-3 border rounded-md text-left text-sm text-gray-700 transition-colors",
              isOpen ? "border-[#F5CB2B] bg-[#F2F2F2]" : "border-gray-300 bg-white hover:border-[#F5CB2B] hover:bg-[#F2F2F2]",
              selectedOption ? "border-yellow-500" : "",
            )}
            onClick={() => setIsOpen((o) => !o)}
          >
            <span className="flex-1 truncate">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            {allowClear && selectedOption ? (
              <XIcon
                aria-hidden
                className="text-gray-500 w-4 h-4 mr-1 hover:text-gray-700"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              />
            ) : null}
            <ChevronDownIcon className="text-gray-500 w-5 h-5 ml-1" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          style={{
            minWidth: buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "auto",
            maxHeight: "320px",
            overflow: "hidden",
          }}
        >
          <div className="flex flex-col">
            {/* Search bar */}
            <div className="sticky top-0 z-10 bg-white border-b">
              <div className="relative p-2">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="pl-9 h-9"
                  aria-label="Campo de busca"
                />
              </div>
            </div>

            {/* Options list */}
            <div
              role="listbox"
              aria-label={label || "Opções"}
              className="max-h-[270px] overflow-y-auto"
            >
              {allowClear && selectedOption && filteredOptions.length > 0 && (
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                  onClick={handleClear}
                >
                  Limpar seleção
                </button>
              )}

              {filteredOptions.length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-500 text-center">
                  {emptySearchText}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const selected = selectedOption?.value === option.value;
                  return (
                    <button
                      key={option.value}
                      role="option"
                      aria-selected={selected}
                      type="button"
                      className={cn(
                        "px-4 py-2 text-left w-full flex items-center gap-2 transition-colors",
                        "hover:bg-gray-100",
                        selected ? "bg-gray-200" : "",
                      )}
                      onClick={() => handleSelect(option)}>
                      {selected ? (
                        <CheckIcon className="w-4 h-4 text-gray-700" />
                      ) : (
                        <span className="w-4 h-4" />
                      )}
                      <span className="truncate">{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
